import React, { useState, useEffect, useRef } from 'react';
import { CONFIG } from '../config';

const EventManagementPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    event_id: '',
    event_name: '',
    event_code: '',
    sms_phone_number: '',
    whatsapp_phone_number_id: '',
    variables: {},
    template_ids: [],
    lex_enabled: false,
    dj_enabled: false,
    media_enabled: false,
    welcome_notification_enabled: false,
    allowed_channels: 'both',
    custom_groups: ['Bride', 'Groom', 'Bridal Party', 'Event Staff'],
  });
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [channelsOpen, setChannelsOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [varsOpen, setVarsOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState(null);
  const pollTimerRef = useRef(null);

  const statusLabel = s => ({ pending: 'Provisioning AI\u2026', ready: 'AI Ready', error: 'Provisioning Failed' }[s] || '');

  useEffect(() => {
    fetchEvents();
    fetchTemplates();
  }, []);

  const fetchSingleEvent = async (eventId) => {
    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(eventId)}`, {
        credentials: 'include',
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const latest = data.event || data;
      setEditingEvent(latest);
    } catch (err) {
      console.error('Failed to poll event status:', err);
    }
  };

  useEffect(() => {
    if (editingEvent?.ai_provisioning_status === 'pending') {
      pollTimerRef.current = setInterval(() => {
        fetchSingleEvent(editingEvent.event_id);
      }, 5000);
    }
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingEvent?.ai_provisioning_status]);

  const handleProvisionAI = async () => {
    setProvisioning(true);
    setProvisionError(null);
    try {
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(editingEvent.event_id)}/provision-ai`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || data.detail || 'Failed to start provisioning');
      setEditingEvent(prev => ({ ...prev, ai_provisioning_status: 'pending' }));
    } catch (err) {
      setProvisionError(err.message);
    } finally {
      setProvisioning(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/events`, {
        credentials: 'include',
      });

      if (!resp.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await resp.json();
      setEvents(data.events || []);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/templates`, {
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        setAvailableTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      event_id: '',
      event_name: '',
      event_code: '',
      sms_phone_number: '',
      whatsapp_phone_number_id: '',
      variables: {},
      template_ids: [],
      lex_enabled: false,
      dj_enabled: false,
      media_enabled: false,
      welcome_notification_enabled: false,
      allowed_channels: 'both',
      custom_groups: ['Bride', 'Groom', 'Bridal Party', 'Event Staff'],
    });
    setNewVarKey('');
    setNewVarValue('');
    setNewGroup('');
    setChannelsOpen(false);
    setGroupsOpen(false);
    setVarsOpen(false);
    setTemplatesOpen(false);
  };

  const openCreateForm = () => {
    resetForm();
    setEditingEvent(null);
    setShowCreateForm(true);
  };

  const openEditForm = (event) => {
    setFormData({
      event_id: event.event_id,
      event_name: event.event_name || '',
      event_code: event.event_code || '',
      sms_phone_number: event.sms_phone_number || '',
      whatsapp_phone_number_id: event.whatsapp_phone_number_id || '',
      variables: event.variables || {},
      template_ids: event.template_ids || [],
      lex_enabled: !!event.lex_enabled,
      dj_enabled: !!event.dj_enabled,
      media_enabled: !!event.media_enabled,
      welcome_notification_enabled: !!event.welcome_notification_enabled,
      allowed_channels: event.allowed_channels || 'both',
      custom_groups: event.custom_groups || ['Bride', 'Groom', 'Bridal Party', 'Event Staff'],
    });
    setEditingEvent(event);
    setShowCreateForm(true);
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingEvent(null);
    resetForm();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addVariable = () => {
    if (!newVarKey.trim()) return;
    setFormData(prev => ({
      ...prev,
      variables: { ...prev.variables, [newVarKey.trim()]: newVarValue.trim() }
    }));
    setNewVarKey('');
    setNewVarValue('');
  };

  const removeVariable = (key) => {
    setFormData(prev => {
      const updated = { ...prev.variables };
      delete updated[key];
      return { ...prev, variables: updated };
    });
  };

  const addGroup = () => {
    const g = newGroup.trim();
    if (!g || formData.custom_groups.includes(g)) return;
    setFormData(prev => ({ ...prev, custom_groups: [...prev.custom_groups, g] }));
    setNewGroup('');
  };

  const removeGroup = (group) => {
    setFormData(prev => ({ ...prev, custom_groups: prev.custom_groups.filter(g => g !== group) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const isEdit = !!editingEvent;
      const url = isEdit
        ? `${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(editingEvent.event_id)}`
        : `${CONFIG.apiBaseUrl}/admin/events`;

      const payload = isEdit
        ? {
            event_name: formData.event_name,
            event_code: formData.event_code || '',
            sms_phone_number: formData.sms_phone_number || undefined,
            whatsapp_phone_number_id: formData.whatsapp_phone_number_id || undefined,
            variables: Object.keys(formData.variables).length > 0 ? formData.variables : undefined,
            template_ids: formData.template_ids.length > 0 ? formData.template_ids : undefined,
            lex_enabled: formData.lex_enabled,
            dj_enabled: formData.dj_enabled,
            media_enabled: formData.media_enabled,
            welcome_notification_enabled: formData.welcome_notification_enabled,
            allowed_channels: formData.allowed_channels,
            custom_groups: formData.custom_groups,
          }
        : formData;

      const resp = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || data.message || `Failed to ${isEdit ? 'update' : 'create'} event`);
      }

      closeForm();
      fetchEvents();
    } catch (err) {
      console.error('Save failed:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm(`Are you sure you want to delete event "${eventId}"? This will not delete the guest table.`)) {
      return;
    }

    try {
      setError(null);
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(eventId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to delete event');
      }

      fetchEvents();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.message);
    }
  };

  return (
    <div className="admin-page">
      <h2 className="page-title">Event Management</h2>
      <p className="page-subtitle">
        Configure events with their phone numbers, variables, and assigned templates.
      </p>

      {error && <div className="error-message">{error}</div>}

      {!showCreateForm && (
        <div className="settings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Events</h3>
          </div>

          {loading ? (
            <div className="loading-message">Loading events...</div>
          ) : events.length === 0 ? (
            <div className="empty-state">
              <p>No events configured yet. Create your first event to get started.</p>
            </div>
          ) : (
            <div className="event-list">
              {[...events].sort((a, b) => (a.event_name || '').localeCompare(b.event_name || '')).map(event => (
                <div key={event.event_id} className="event-card">
                  <div className="event-card-header">
                    <div>
                      <h4 className="event-card-title">{event.event_name}</h4>
                      <div className="event-card-id">{event.event_id}</div>
                    </div>
                    <div className="event-card-actions">
                      <button onClick={() => openEditForm(event)} className="btn btn-secondary btn-sm">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(event.event_id)} className="btn btn-danger btn-sm">
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="event-card-details">
                    {event.event_code && (
                      <div className="event-detail">
                        <span className="detail-label">Event Code:</span>
                        <span className="detail-value">{event.event_code}</span>
                      </div>
                    )}
                    {event.sms_phone_number && (
                      <div className="event-detail">
                        <span className="detail-label">SMS Number:</span>
                        <span className="detail-value">{event.sms_phone_number}</span>
                      </div>
                    )}
                    {event.whatsapp_phone_number_id && (
                      <div className="event-detail">
                        <span className="detail-label">WhatsApp ID:</span>
                        <span className="detail-value">{event.whatsapp_phone_number_id}</span>
                      </div>
                    )}
                    <div className="event-detail">
                      <span className="detail-label">AI:</span>
                      <span className="detail-value">
                        {event.lex_enabled ? 'Enabled' : 'Disabled'}
                        {event.lex_enabled && event.ai_provisioning_status && (
                          <span className={`status-badge ai-status-${event.ai_provisioning_status}`} style={{ marginLeft: '8px', fontSize: '0.8em' }}>
                            {statusLabel(event.ai_provisioning_status)}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="event-detail">
                      <span className="detail-label">#HeyDJ:</span>
                      <span className="detail-value">{event.dj_enabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="event-detail">
                      <span className="detail-label">Media:</span>
                      <span className="detail-value">{event.media_enabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <br />
          <div>
            <button onClick={openCreateForm} className="btn btn-primary">
              + New Event
            </button>
          </div>
        </div>
      )}

      {showCreateForm && (
        <div className="settings-section">
          <h3>{editingEvent ? 'Edit Event' : 'Create New Event'}</h3>
          <form onSubmit={handleSubmit}>
            {!editingEvent && (
              <div className="form-group">
                <label className="form-label">Event ID (slug)</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.event_id}
                  onChange={(e) => handleInputChange('event_id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="e.g., smith-wedding-2026"
                  required
                  pattern="[a-z0-9][a-z0-9-]{2,48}[a-z0-9]"
                  title="4-50 characters, lowercase letters, numbers, and hyphens only"
                />
                <div className="input-hint">Lowercase letters, numbers, and hyphens only. This cannot be changed later.</div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Event Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.event_name}
                onChange={(e) => handleInputChange('event_name', e.target.value)}
                placeholder="e.g., Smith Wedding 2026"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Event Code (for public registration)</label>
              <input
                type="text"
                className="form-input"
                value={formData.event_code}
                onChange={(e) => handleInputChange('event_code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="e.g., SMITH2026"
                maxLength={20}
              />
              <div className="input-hint">Short code guests can enter at /addme to register for this event. Letters and numbers only, auto-uppercased. Leave blank to disable code-based registration.</div>
            </div>

            <div className="form-group">
              <button
                type="button"
                onClick={() => setChannelsOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontSize: '0.95rem', fontWeight: 600, color: 'inherit',
                }}
              >
                <span style={{ fontSize: '0.75rem', transition: 'transform 0.2s', display: 'inline-block', transform: channelsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                Channel Settings
              </button>
              {channelsOpen && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label className="form-label">Allowed Channels</label>
                    <select
                      className="form-input"
                      value={formData.allowed_channels}
                      onChange={(e) => handleInputChange('allowed_channels', e.target.value)}
                    >
                      <option value="both">SMS &amp; WhatsApp</option>
                      <option value="sms">SMS Only</option>
                      <option value="whatsapp">WhatsApp Only</option>
                    </select>
                    <div className="input-hint">Controls which messaging channels are available for this event — affects navigation, guest registration, and message preference options.</div>
                  </div>
                  <div>
                    <label className="form-label">SMS Phone Number (Twilio)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.sms_phone_number}
                      onChange={(e) => handleInputChange('sms_phone_number', e.target.value)}
                      placeholder="e.g., +18005551234"
                    />
                    <div className="input-hint">Twilio FROM number in E.164 format. Leave blank to use global default.</div>
                  </div>
                  <div>
                    <label className="form-label">WhatsApp Phone Number ID</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.whatsapp_phone_number_id}
                      onChange={(e) => handleInputChange('whatsapp_phone_number_id', e.target.value)}
                      placeholder="e.g., 123456789012345"
                    />
                    <div className="input-hint">WhatsApp Business phone number ID. Leave blank to use global default.</div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <button
                type="button"
                onClick={() => setGroupsOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontSize: '0.95rem', fontWeight: 600, color: 'inherit',
                }}
              >
                <span style={{ fontSize: '0.75rem', transition: 'transform 0.2s', display: 'inline-block', transform: groupsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                Guest Groups
                <span style={{ fontWeight: 400, fontSize: '0.85rem', color: '#6b7280' }}>
                  ({formData.custom_groups.length + 1})
                </span>
              </button>
              {groupsOpen && (
                <>
                  <div className="variables-list" style={{ marginTop: '8px' }}>
                    <div className="variable-row">
                      <span className="variable-key" style={{ color: '#9ca3af' }}>All</span>
                      <span style={{ fontSize: '0.8em', color: '#9ca3af', fontStyle: 'italic' }}>always included</span>
                    </div>
                    {formData.custom_groups.map(group => (
                      <div key={group} className="variable-row">
                        <span className="variable-key">{group}</span>
                        <button type="button" onClick={() => removeGroup(group)} className="btn btn-danger btn-sm">
                          X
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="variable-add-row">
                    <input
                      type="text"
                      className="form-input form-input-sm"
                      value={newGroup}
                      onChange={(e) => setNewGroup(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGroup(); } }}
                      placeholder="New group (e.g., VIP, Speaker)"
                    />
                    <button type="button" onClick={addGroup} className="btn btn-secondary btn-sm" disabled={!newGroup.trim()}>
                      Add
                    </button>
                  </div>
                  <div className="input-hint">"All" is always available. Add event-specific groups like Bride, Groom, VIP, etc.</div>
                </>
              )}
            </div>

            <div className="form-group">
              <button
                type="button"
                onClick={() => setVarsOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontSize: '0.95rem', fontWeight: 600, color: 'inherit',
                }}
              >
                <span style={{ fontSize: '0.75rem', transition: 'transform 0.2s', display: 'inline-block', transform: varsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                Variables
                <span style={{ fontWeight: 400, fontSize: '0.85rem', color: '#6b7280' }}>
                  ({Object.keys(formData.variables).length})
                </span>
              </button>
              {varsOpen && (
                <>
                  <div className="variables-list" style={{ marginTop: '8px' }}>
                    {Object.entries(formData.variables).map(([key, value]) => (
                      <div key={key} className="variable-row">
                        <span className="variable-key">{key}</span>
                        <input
                          type="text"
                          className="form-input form-input-sm"
                          value={value}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            variables: { ...prev.variables, [key]: e.target.value },
                          }))}
                          placeholder="Value"
                        />
                        <button type="button" onClick={() => removeVariable(key)} className="btn btn-danger btn-sm">
                          X
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="variable-add-row">
                    <input
                      type="text"
                      className="form-input form-input-sm"
                      value={newVarKey}
                      onChange={(e) => setNewVarKey(e.target.value)}
                      placeholder="Key (e.g., bride_name)"
                    />
                    <input
                      type="text"
                      className="form-input form-input-sm"
                      value={newVarValue}
                      onChange={(e) => setNewVarValue(e.target.value)}
                      placeholder="Value (e.g., Sarah)"
                    />
                    <button type="button" onClick={addVariable} className="btn btn-secondary btn-sm" disabled={!newVarKey.trim()}>
                      Add
                    </button>
                  </div>
                  <div className="input-hint">Custom variables for templates (e.g., bride_name, groom_name, venue).</div>
                </>
              )}
            </div>

            <div className="form-group">
              <button
                type="button"
                onClick={() => setTemplatesOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontSize: '0.95rem', fontWeight: 600, color: 'inherit',
                }}
              >
                <span style={{ fontSize: '0.75rem', transition: 'transform 0.2s', display: 'inline-block', transform: templatesOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                Assigned Templates
                <span style={{ fontWeight: 400, fontSize: '0.85rem', color: '#6b7280' }}>
                  ({formData.template_ids.length})
                </span>
              </button>
              {templatesOpen && (
                <>
                  {availableTemplates.length === 0 ? (
                    <div className="input-hint" style={{ marginTop: '8px' }}>No templates available. Create templates first in Message Templates.</div>
                  ) : (
                    <div className="template-checkbox-group" style={{ marginTop: '8px' }}>
                      {[...availableTemplates].sort((a, b) => a.template_name.localeCompare(b.template_name)).map(tmpl => (
                        <label key={tmpl.template_id} className="template-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.template_ids.includes(tmpl.template_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newVars = {};
                                (tmpl.variables || []).forEach(v => { if (!(v in formData.variables)) newVars[v] = ''; });
                                setFormData(prev => ({
                                  ...prev,
                                  template_ids: [...prev.template_ids, tmpl.template_id],
                                  variables: { ...newVars, ...prev.variables },
                                }));
                              } else {
                                handleInputChange('template_ids', formData.template_ids.filter(id => id !== tmpl.template_id));
                              }
                            }}
                          />
                          <span className="template-checkbox-name">{tmpl.template_name}</span>
                          <span className="template-checkbox-meta">({tmpl.channel} | {tmpl.event_type})</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="input-hint">Select message templates to associate with this event.</div>
                </>
              )}
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <label className="template-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.lex_enabled}
                    onChange={(e) => handleInputChange('lex_enabled', e.target.checked)}
                  />
                  <span className="template-checkbox-name">Enable AI</span>
                </label>
                {formData.lex_enabled && editingEvent?.ai_provisioning_status && (
                  <span className={`status-badge ai-status-${editingEvent.ai_provisioning_status}`}>
                    {statusLabel(editingEvent.ai_provisioning_status)}
                  </span>
                )}
              </div>
              <div className="input-hint">When enabled, inbound messages from guests in this event will be processed by Amazon Lex and receive automated replies.</div>
            </div>

            {formData.lex_enabled && (
              <>
                {editingEvent?.ai_provisioning_status === 'error' && (
                  <div className="error-message" style={{ marginTop: '0', marginBottom: '16px' }}>
                    AI provisioning failed: {editingEvent.ai_provisioning_error || 'Check CloudWatch logs for provisionEventAI'}
                  </div>
                )}
                {editingEvent && editingEvent.lex_enabled && !editingEvent.lex_bot_id && editingEvent.ai_provisioning_status !== 'pending' && (
                  <div className="form-group">
                    {provisionError && (
                      <div className="error-message" style={{ marginBottom: '8px' }}>{provisionError}</div>
                    )}
                    <button
                      type="button"
                      onClick={handleProvisionAI}
                      disabled={provisioning}
                      className="btn btn-secondary"
                    >
                      {provisioning ? 'Starting...' : 'Create Lex Resources'}
                    </button>
                    <div className="input-hint">Provisions the Lex bot, Bedrock Agent, and Knowledge Base for this event. This may take several minutes.</div>
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label className="template-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.dj_enabled}
                  onChange={(e) => handleInputChange('dj_enabled', e.target.checked)}
                />
                <span className="template-checkbox-name">Enable #HeyDJ Song Requests</span>
              </label>
              <div className="input-hint">When enabled, guests can submit song requests via SMS/WhatsApp (#HeyDJ) and the web form. When disabled, #HeyDJ messages are ignored and public DJ pages return an error.</div>
            </div>

            <div className="form-group">
              <label className="template-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.media_enabled}
                  onChange={(e) => handleInputChange('media_enabled', e.target.checked)}
                />
                <span className="template-checkbox-name">Enable Media Collection</span>
              </label>
              <div className="input-hint">When enabled, media files (photos, videos) from guests are downloaded and stored. When disabled, media attachments are ignored and the media page shows no new uploads.</div>
            </div>

            <div className="form-group">
              <label className="template-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.welcome_notification_enabled}
                  onChange={(e) => handleInputChange('welcome_notification_enabled', e.target.checked)}
                />
                <span className="template-checkbox-name">Welcome Notification on Signup</span>
              </label>
              <div className="input-hint">When enabled, new guests who register via the /addme page will automatically receive a welcome message using the <strong>core-sub-confirmation-plain</strong> template, populated with this event's variables. The message is sent on the channel(s) the guest selected at signup.</div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
              </button>
              <button type="button" onClick={closeForm} className="btn btn-secondary" disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EventManagementPage;
